/**
 * DOG OWNED — the local dog profile.
 *
 * One dog, one device, one key, no backend. This stores the customer's own
 * answers so the shop can say "Shopping for Alfie" on their next visit — it is
 * a convenience, not an account, and it makes no cross-device promise.
 *
 * What it deliberately never stores: health details, free-text care concerns,
 * anything resembling a diagnosis, an email, an account identifier, or any
 * Shopify GID. Answers are stored; route IDs are derived at query time. That
 * way a stored profile can never pin the shop to a route that has since been
 * renamed or removed, and a copied localStorage value reveals nothing about
 * the dog beyond what the customer typed.
 *
 * localStorage rather than sessionStorage: the value of remembering a dog is
 * precisely that it outlives the tab. sessionStorage would forget between
 * visits, which is the one thing this exists to avoid. Nothing here is
 * sensitive enough to warrant the shorter life, and Forget is always one click
 * away.
 *
 * The shape is versioned. Anything that is not version 1 is discarded rather
 * than migrated or repaired — a wrong guess about old data is worse than a
 * clean start, and the customer only loses four answers they can re-give.
 */
window.DogProfile = (function () {
  'use strict';

  var KEY = 'dog_owned_profile_v1';
  var VERSION = 1;
  var NAME_MAX = 32;

  /**
   * Every storage call is wrapped: Safari private mode throws on setItem, and
   * some privacy modes throw on read. A shop that cannot remember a dog must
   * still sell things, so failure here is always silent and non-fatal.
   */
  function safeGet() {
    try {
      return window.localStorage.getItem(KEY);
    } catch (error) {
      return null;
    }
  }

  function safeSet(value) {
    try {
      window.localStorage.setItem(KEY, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function safeRemove() {
    try {
      window.localStorage.removeItem(KEY);
    } catch (error) {
      /* Nothing to do — a profile we cannot remove is one we simply stop reading. */
    }
  }

  function cleanName(value) {
    if (typeof value !== 'string') return '';
    return value.trim().slice(0, NAME_MAX);
  }

  function cleanList(value) {
    if (!Array.isArray(value)) return [];
    return value.filter(function (item) {
      return typeof item === 'string' && item.length > 0 && item.length < 64;
    });
  }

  function cleanAnswer(value) {
    if (typeof value !== 'string') return '';
    return value.length < 32 ? value : '';
  }

  /**
   * Read a profile, or null. Malformed JSON, a wrong version, a non-object or
   * an array all resolve the same way: remove it and start fresh, with no
   * technical error shown to the customer.
   */
  function read() {
    var raw = safeGet();
    if (!raw) return null;

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      safeRemove();
      return null;
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      safeRemove();
      return null;
    }

    if (parsed.version !== VERSION) {
      safeRemove();
      return null;
    }

    // Re-clean on the way out as well as in. Storage is user-writable, so what
    // went in is not necessarily what comes back.
    return {
      version: VERSION,
      name: cleanName(parsed.name),
      size: cleanAnswer(parsed.size),
      life_stage: cleanAnswer(parsed.life_stage),
      characteristics: cleanList(parsed.characteristics),
      goal: cleanAnswer(parsed.goal),
      created_at: typeof parsed.created_at === 'string' ? parsed.created_at : '',
      updated_at: typeof parsed.updated_at === 'string' ? parsed.updated_at : ''
    };
  }

  /**
   * Persist a completed set of answers. Only ever called once the customer has
   * actually reached results — a half-answered journey is not a dog.
   */
  function write(answers) {
    var now = new Date().toISOString();
    var existing = read();

    var profile = {
      version: VERSION,
      name: cleanName(answers.name),
      size: cleanAnswer(answers.size),
      life_stage: cleanAnswer(answers.life_stage),
      characteristics: cleanList(answers.characteristics),
      goal: cleanAnswer(answers.goal),
      created_at: existing && existing.created_at ? existing.created_at : now,
      updated_at: now
    };

    return safeSet(JSON.stringify(profile)) ? profile : null;
  }

  /**
   * Forget removes this key and only this key. Unrelated localStorage belongs
   * to the customer and to other features — clearing it would be overreach.
   */
  function forget() {
    safeRemove();
  }

  return { read: read, write: write, forget: forget, KEY: KEY, NAME_MAX: NAME_MAX };
})();
